// Device/session/identity utilities.
// getDeviceId() — persistent UUID in localStorage (rsvp_device_id), secondary RSVP identity
//   fallback when no phone number is present.
// getSessionId() — ephemeral UUID in sessionStorage, audit logs only.
// getDeviceInfo() — device/network snapshot for audit logs only (NOT used for identity).
export const getDeviceInfo = async () => {
  const deviceInfo = {
    userAgent: navigator.userAgent,
    platform: navigator.platform,
    language: navigator.language,
    languages: navigator.languages,
    screenResolution: `${window.screen.width}x${window.screen.height}`,
    colorDepth: window.screen.colorDepth,
    pixelRatio: window.devicePixelRatio,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    timezoneOffset: new Date().getTimezoneOffset(),
    touchSupport: 'ontouchstart' in window,
    cpuCores: navigator.hardwareConcurrency || 'unknown',
    memory: navigator.deviceMemory || 'unknown',
    connection: getConnectionInfo(),
    vendor: navigator.vendor,
    cookiesEnabled: navigator.cookieEnabled,
    doNotTrack: navigator.doNotTrack,
    referrer: document.referrer || 'direct',
    timestamp: new Date().toISOString(),
    ipAddress: 'unavailable',
    location: 'unavailable'
  };

  // Await IP and geo — required for IP-based ban checks.
  // Capped at 1 second so a slow/blocked lookup doesn't stall the submit path.
  try {
    const result = await Promise.race([
      getIPAndGeo(),
      new Promise(resolve => setTimeout(() => resolve({ ip: 'unavailable', geo: 'unavailable' }), 1000))
    ]);
    if (result.ip && result.ip !== 'unavailable') deviceInfo.ipAddress = result.ip;
    if (result.geo && result.geo !== 'unavailable') deviceInfo.location = result.geo;
  } catch {
    // Silently fail — IP is nice-to-have but not required for non-ban paths
  }

  return deviceInfo;
};

const getIPAndGeo = async () => {
  const result = { ip: 'unavailable', geo: 'unavailable' };
  
  try {
    const ipResponse = await fetch('https://api.ipify.org?format=json', { 
      signal: AbortSignal.timeout(800)
    });
    const ipData = await ipResponse.json();
    result.ip = ipData.ip;
  } catch (error) {
    // Blocked by ad blocker or network error - that's OK
  }

  try {
    const geoResponse = await fetch('https://ipapi.co/json/', { 
      signal: AbortSignal.timeout(800)
    });
    const geoData = await geoResponse.json();
    result.geo = {
      city: geoData.city,
      region: geoData.region,
      country: geoData.country_name,
      postal: geoData.postal,
      latitude: geoData.latitude,
      longitude: geoData.longitude,
      org: geoData.org,
      asn: geoData.asn
    };
  } catch (error) {
    // Blocked or error - that's OK
  }

  return result;
};

const getConnectionInfo = () => {
  const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  if (connection) {
    return {
      effectiveType: connection.effectiveType,
      downlink: connection.downlink,
      rtt: connection.rtt,
      saveData: connection.saveData
    };
  }
  return 'unavailable';
};

// Create a unique session ID for tracking
export const getSessionId = () => {
  let sessionId = sessionStorage.getItem('rsvp_session_id');
  if (!sessionId) {
    sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem('rsvp_session_id', sessionId);
  }
  return sessionId;
};

// Get or create a persistent device ID
export const getDeviceId = () => {
  let deviceId = localStorage.getItem('rsvp_device_id');
  if (!deviceId) {
    deviceId = `device_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem('rsvp_device_id', deviceId);
  }
  return deviceId;
};
